import React from "react";
import styled from "styled-components";
import { Form, Formik, FormikHelpers } from "formik";
import * as Sentry from "@sentry/react";
import * as Yup from "yup";
import Parser from "html-react-parser";
import TextLink from "../library/TextLink";
import { RichTextField } from "../library/Form";
import { RichTextElementStyles } from "../library/RichTextEditor/RichTextEditor";
import SubmitButton from "../library/Form/SubmitButton";
import RecruiterUser from "../../apiResources/RecruiterUser";
import EmployerUser from "../../apiResources/EmployerUser";
import Skeleton from "../library/Skeleton";
import SettingLabel from "./SettingLabel";

const BiographySettingOption = <User extends RecruiterUser | EmployerUser>(
  props: Props<User>
) => {
  const {
    user,
    isEditing = false,
    disabled = false,
    isLoading = false,
    onEdit,
    onDone,
    onUserChange,
  } = props;

  const save = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    if (values.biography === user.biography) {
      onDone();
      return;
    }

    try {
      user.assignAttributes(values);
      const success = await user.save();
      if (success) {
        onUserChange?.(user);
        onDone();
      } else {
        formik.setErrors({
          biography: user.errors.biography?.fullMessage,
        });
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const edit = () => {
    if (!disabled) {
      onEdit();
    }
  };

  const value = user?.biography;
  const showNotSpecified = !isLoading && !value;
  const showValue = !isLoading && Boolean(value);

  if (!isEditing) {
    return (
      <section>
        <Header>
          <SettingLabel>Biography</SettingLabel>
          <TextLink disabled={disabled} onClick={edit}>
            Edit
          </TextLink>
        </Header>
        {isLoading && <Skeleton height="100" />}
        {showNotSpecified && <div>Not specified</div>}
        {showValue && <BiographyDisplay>{Parser(value)}</BiographyDisplay>}
      </section>
    );
  }

  const initialValues: FormValues = {
    biography: user.biography,
  };

  const validation = Yup.object({
    biography: Yup.string().required("Please enter your biography"),
  });

  return (
    <section>
      <Formik
        initialValues={initialValues}
        onSubmit={save}
        validationSchema={validation}
      >
        <Form autoComplete="off">
          <Header>
            <SettingLabel>Biography</SettingLabel>
            <TextLink onClick={onDone}>Cancel</TextLink>
          </Header>
          <RichTextField name="biography" label="Biography" hiddenLabel />
          <SubmitButton />
        </Form>
      </Formik>
    </section>
  );
};

type Props<User> = {
  user: User;
  isEditing?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  onEdit: () => void;
  onDone: () => void;
  onUserChange?: (user: User) => void;
};

type FormValues = {
  biography: string;
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const BiographyDisplay = styled.div`
  ${RichTextElementStyles}
`;

export default BiographySettingOption;
